import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Auth } from 'aws-amplify';
import { useNavigate } from 'react-router-dom';

import AdminNavTabs from '../components/AdminNavTabs';
import './AdminDashboard.css';

const PROJECT_NAME = process.env.REACT_APP_PROJECT_NAME || 'AppDemo';
const API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || '';

export default function AdminPos() {
  const navigate = useNavigate();
  const statusRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState('');

  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  const [envName, setEnvName] = useState('SANDBOX');
  const [posStatus, setPosStatus] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [busy, setBusy] = useState(false);
  const [injectResult, setInjectResult] = useState(null);
  const [injectVariationId, setInjectVariationId] = useState('');
  const [syncedProducts, setSyncedProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('');

  const redirectUri = useMemo(() => {
    try {
      return `${window.location.origin}/admin/channels/pos/callback`;
    } catch {
      return '';
    }
  }, []);

  useEffect(() => {
    if (!statusMessage && !error) return;
    try {
      window.setTimeout(() => {
        try {
          statusRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
          statusRef.current?.focus?.({ preventScroll: true });
        } catch {
          // ignore
        }
      }, 50);
    } catch {
      // ignore
    }
  }, [statusMessage, error]);

  useEffect(() => {
    const init = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const session = await Auth.currentSession();
        const groups = session.getIdToken().payload['cognito:groups'] || [];

        if (!groups.includes('Admins')) {
          navigate('/admin/login');
          return;
        }

        setUsername(user.username || user.attributes?.email || 'Admin');
        await fetchStatus(envName);
      } catch {
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchSyncedProducts = async () => {
    if (!API_BASE_URL) return;
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/products`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load products');

      const products = Array.isArray(data?.products) ? data.products : [];
      const synced = products.filter(
        (p) => p?.SquareCatalogVariationId && p?.SquareCatalogItemId
      );
      setSyncedProducts(synced);
      if (!selectedProductId && synced.length > 0) {
        setSelectedProductId(String(synced[0].ProductID || ''));
      }
    } catch (e) {
      setSyncedProducts([]);
      setError(e?.message || 'Failed to load products');
    }
  };

  useEffect(() => {
    if (loading) return;
    fetchStatus(envName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envName]);

  useEffect(() => {
    if (loading) return;
    if (!posStatus?.connected) return;
    fetchLocations(envName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envName, posStatus?.connected]);

  useEffect(() => {
    if (loading) return;
    if (!posStatus?.connected) return;
    fetchSyncedProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posStatus?.connected]);

  const fetchStatus = async (env) => {
    if (!API_BASE_URL) return;
    try {
      setError('');
      setStatusMessage('');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/pos/status?environment=${encodeURIComponent(env)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load POS status');
      setPosStatus(data);
      if (data?.posLocationId) {
        setSelectedLocationId(String(data.posLocationId));
      }
    } catch (e) {
      setError(e?.message || 'Failed to load POS status');
      setPosStatus(null);
      setLocations([]);
    }
  };

  const injectTestOrder = async () => {
    if (!API_BASE_URL) return;
    if (busy) return;
    if (!posStatus?.connected) return;
    if (!selectedLocationId) return;

    setBusy(true);
    try {
      setError('');
      setStatusMessage('');
      setInjectResult(null);

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const selectedProduct = syncedProducts.find(
        (p) => String(p?.ProductID || '') === String(selectedProductId || '')
      );
      const selectedVariationId = String(selectedProduct?.SquareCatalogVariationId || '').trim();
      const trimmedVariationId = String(injectVariationId || selectedVariationId || '').trim();
      const items = [
        {
          name: 'Test Item',
          quantity: 1,
          price_cents: 100,
          currency: 'USD',
          ...(trimmedVariationId ? { SquareCatalogVariationId: trimmedVariationId } : {}),
        },
      ];

      const res = await fetch(`${API_BASE_URL}/admin/pos/order/inject`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          environment: envName,
          username,
          posLocationId: selectedLocationId,
          notes: 'Admin POS test order',
          items,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to inject test order');

      setInjectResult(data);
      if (data?.injected) {
        setStatusMessage('Test order injected into Square. Check Square Sandbox dashboard orders.');
      } else {
        setError(data?.reason || 'Test order was not injected.');
      }
    } catch (e) {
      setError(e?.message || 'Failed to inject test order');
    } finally {
      setBusy(false);
    }
  };

  const fetchLocations = async (env) => {
    if (!API_BASE_URL) return;
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/pos/locations?environment=${encodeURIComponent(env)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load POS locations');

      const locs = Array.isArray(data?.locations) ? data.locations : [];
      setLocations(locs);

      if (data?.posLocationId) {
        setSelectedLocationId(String(data.posLocationId));
      }
    } catch (e) {
      setLocations([]);
      setError(e?.message || 'Failed to load POS locations');
    }
  };

  const saveLocation = async (locationId) => {
    if (!API_BASE_URL) return;
    if (!locationId) return;
    if (busy) return;

    setBusy(true);
    try {
      setError('');
      setStatusMessage('');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/pos/location`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ environment: envName, location_id: locationId }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to save POS location');

      setSelectedLocationId(locationId);
      setStatusMessage('POS location saved.');
      await fetchStatus(envName);
    } catch (e) {
      setError(e?.message || 'Failed to save POS location');
    } finally {
      setBusy(false);
    }
  };

  const startConnect = async () => {
    if (!API_BASE_URL) return;
    if (busy) return;
    setBusy(true);

    try {
      setError('');
      setStatusMessage('');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/pos/connect/start`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          provider: 'SQUARE',
          environment: envName,
          redirect_uri: redirectUri,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to start POS connect');

      const url = String(data?.authorize_url || '').trim();
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
        setStatusMessage('Square connect opened in a new tab. Complete authorization, then return here.');
      } else {
        setStatusMessage('Connect started, but no authorization URL was returned.');
      }
    } catch (e) {
      setError(e?.message || 'Failed to start POS connect');
    } finally {
      setBusy(false);
    }
  };

  const refresh = async () => {
    await fetchStatus(envName);
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <header className="dashboard-header">
          <h1>{PROJECT_NAME} Admin</h1>
          <p>Loading…</p>
        </header>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <header className="dashboard-header">
        <h1>{PROJECT_NAME} Admin</h1>
        <p>
          Welcome, <strong>{username}</strong>
        </p>
      </header>

      <AdminNavTabs />

      <main className="dashboard-content">
        <section className="dashboard-section">
          <h2>POS (Square)</h2>

          <div ref={statusRef} tabIndex={-1} />
          {error ? <p className="error-message">{error}</p> : null}
          {statusMessage ? <p className="success-message">{statusMessage}</p> : null}

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <label>
              Environment
              <select value={envName} onChange={(e) => setEnvName(e.target.value)} style={{ marginLeft: 8 }}>
                <option value="SANDBOX">SANDBOX</option>
                <option value="PRODUCTION">PRODUCTION</option>
              </select>
            </label>

            <label>
              Location
              <select
                value={selectedLocationId}
                onChange={(e) => {
                  const next = e.target.value;
                  setSelectedLocationId(next);
                  saveLocation(next);
                }}
                style={{ marginLeft: 8, minWidth: 220 }}
                disabled={!posStatus?.connected || locations.length === 0 || busy}
              >
                <option value="">Select…</option>
                {locations.map((l) => (
                  <option key={String(l?.id || '')} value={String(l?.id || '')}>
                    {String(l?.name || l?.id || '')}
                  </option>
                ))}
              </select>
            </label>

            <button type="button" className="admin-btn" onClick={refresh} disabled={busy}>
              Refresh status
            </button>

            <button type="button" className="admin-btn" onClick={startConnect} disabled={busy || !redirectUri}>
              Connect Square
            </button>

            <button
              type="button"
              className="admin-btn"
              onClick={injectTestOrder}
              disabled={busy || !posStatus?.connected || !selectedLocationId}
            >
              Inject test order
            </button>
          </div>

          <div style={{ marginTop: 12, maxWidth: 520, display: 'grid', gap: 10 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              Sync-enabled product
              <select
                value={selectedProductId}
                onChange={(e) => setSelectedProductId(e.target.value)}
                disabled={syncedProducts.length === 0}
                style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(15,23,42,0.2)' }}
              >
                {syncedProducts.length === 0 ? (
                  <option value="">No synced products found</option>
                ) : (
                  syncedProducts.map((p) => (
                    <option key={p.ProductID} value={String(p.ProductID || '')}>
                      {p.ProductName} · {p.Category}
                    </option>
                  ))
                )}
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              Square Catalog Variation ID (override)
              <input
                type="text"
                value={injectVariationId}
                onChange={(e) => setInjectVariationId(e.target.value)}
                placeholder="Leave empty to use selected product"
                style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(15,23,42,0.2)' }}
              />
            </label>
            <div className="field-hint">
              Select a synced product to inject its SquareCatalogVariationId, or override manually.
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <div className="dashboard-card">
              <h3>Status</h3>
              <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(posStatus, null, 2)}</pre>
            </div>

            {injectResult ? (
              <div className="dashboard-card" style={{ marginTop: 12 }}>
                <h3>Inject result</h3>
                <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(injectResult, null, 2)}</pre>
              </div>
            ) : null}
          </div>
        </section>
      </main>
    </div>
  );
}
